import React, { useMemo } from 'react';

// Presentation pieces of the model-wise claims chart: the bar tooltip and the part-wise breakdown modal.

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const TimeSeriesTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-primary p-4 border border-primary rounded-none shadow-none">
      <p className="font-semibold text-white text-xs uppercase tracking-widest mb-2">{label}</p>
      {payload.map((pld, i) => (
        <div key={i} style={{ color: pld.fill }} className="text-[10px] uppercase tracking-widest">
          {`${pld.name}: ${Number(pld.value ?? 0).toLocaleString()}`}
        </div>
      ))}
      <div className="text-[8px] text-gray-400 mt-3 uppercase tracking-widest">Click a bar to see part-wise details.</div>
    </div>
  );
};

const PartsTable = ({ title, parts }) => {
  const rows = useMemo(() => {
    const arr = Object.entries(parts || {}).map(([name, count]) => ({ name, count: Number(count || 0) }));
    arr.sort((a, b) => b.count - a.count);
    return arr;
  }, [parts]);

  if (!rows.length) return (
    <div className="text-[10px] text-gray-500 uppercase tracking-widest p-3">No data</div>
  );

  return (
    <div className="rounded-none border border-line overflow-hidden">
      <div className="px-3 py-2 bg-background text-[10px] uppercase tracking-widest font-semibold text-primary">{title}</div>
      <div className="max-h-64 overflow-auto">
        <table className="min-w-full text-[10px] uppercase tracking-widest">
          <thead className="bg-white sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 border-b border-line">Part</th>
              <th className="text-right px-3 py-2 border-b border-line">Qty</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className="odd:bg-white even:bg-background">
                <td className="px-3 py-2">{r.name}</td>
                <td className="px-3 py-2 text-right font-semibold">{r.count.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const PartWiseClaimsModal = ({ open, onClose, header, modelName, actualParts, predictedParts }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div className="absolute inset-0 bg-primary/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-[95%] max-w-4xl rounded-none border border-line p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">{header}</div>
            <h3 className="text-sm font-light text-primary uppercase tracking-widest">
              {modelName.replace(/_/g, ' ')} — Parts Breakdown
            </h3>
          </div>
          <button onClick={onClose}
                  className="p-2 rounded-none hover:bg-background transition text-primary" aria-label="Close">
            <XIcon />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <PartsTable title="Actual Parts (till last month)" parts={actualParts} />
          <PartsTable title="Predicted Parts" parts={predictedParts} />
        </div>

        <div className="mt-6 text-[10px] text-gray-500 uppercase tracking-widest">
          Note: Actuals include data up to the end of last month only. Current and future dates/months will not show actual parts.
        </div>
      </div>
    </div>
  );
};
